/*
 * Core logic for running and managing remote chats.
 */

import { assert } from '~/utils/nut';
import { type Message, DISCOVERY_RESPONSE_CATEGORY, USER_RESPONSE_CATEGORY } from '~/lib/persistence/message';
import { chatStore } from '~/lib/stores/chat';
import { sendChatMessageMocked, usingMockChat } from './MockChat';
import { callNutAPI } from './NutAPI';
import { createScopedLogger } from '~/utils/logger';
import { waitForTime } from '~/utils/nut';
import type { ChatResponse } from '~/lib/persistence/response';
import { getLastResponseTime } from './ResponseFilter';
import type { SimulationData } from './MessageHandler';
import { workbenchStore } from '~/lib/stores/workbench';
import { flushSimulationData } from '~/components/chat/ChatComponent/functions/flushSimulationData';

const logger = createScopedLogger('ChatMessage');

export type ChatResponseCallback = (response: ChatResponse) => void;

export enum ChatMode {
  UserMessage = 'UserMessage',
  DevelopApp = 'DevelopApp',
}

// Information about a component referenced on a page during a visit.
export interface ChatReferenceComponent {
  // React component hierarchy down to the referenced element. The first component is
  // the root, and subsequent entries will contain page names, containers etc going
  // down to the referenced component.
  componentNames: string[];
}

// Information describing a user's visit to the app.
export interface VisitData {
  repositoryId: string;
  componentReference?: ChatReferenceComponent;
  simulationData?: SimulationData;
}

export interface NutChatRequest {
  appId: string;
  mode?: ChatMode;
  messages?: Message[];
  visit?: VisitData;
  retryBugReportName?: string;
  upFrontPricing?: boolean;
  payFeatures?: boolean;
}

// Messages that are rendered normally in the chat.
export function shouldDisplayMessage(message: Message) {
  return (
    message.role == 'user' ||
    message.category == DISCOVERY_RESPONSE_CATEGORY ||
    message.category == USER_RESPONSE_CATEGORY
  );
}

const SHORT_POLL_INTERVAL = 10000;

// We use a belt-and-suspenders approach to gathering responses where we both do
// long polling while calling the Nut API and occasional short polling to see if
// there are other responses we haven't received yet for some reason. This covers
// for cases where long polling does not return responses in a timely fashion.
async function pollResponses(appId: string, onResponse: ChatResponseCallback, callback: () => Promise<void>) {
  const getResponses = async () => {
    const lastResponseTime = getLastResponseTime();
    const { responses } = await callNutAPI('get-app-responses', { appId, lastResponseTime });
    for (const response of responses) {
      onResponse(response);
    }
  };

  const interval = setInterval(getResponses, SHORT_POLL_INTERVAL);

  try {
    await callback();
  } finally {
    clearInterval(interval);
    await getResponses();
  }
}

export async function sendChatMessage(request: NutChatRequest, onResponse: ChatResponseCallback) {
  if (usingMockChat()) {
    await sendChatMessageMocked(onResponse);
    return;
  }

  if (request.mode == ChatMode.UserMessage) {
    const repositoryId = workbenchStore.repositoryId.get();
    if (repositoryId) {
      const simulationData = await flushSimulationData();
      if (!request.visit) {
        request.visit = { repositoryId };
      }
      request.visit.simulationData = simulationData;
    }
  }

  logger.debug('sendChatMessage', JSON.stringify(request));

  const appId = chatStore.currentAppId.get();
  assert(appId, 'No app id');

  await pollResponses(appId, onResponse, () => callNutAPI('chat', request, onResponse));

  logger.debug('sendChatMessage finished');
}

// Get all existing responses for the app.
export async function getExistingAppResponses(appId: string): Promise<ChatResponse[]> {
  const { responses } = await callNutAPI('get-app-responses', { appId });
  return responses;
}

// Stream any responses from ongoing work that is modifying the app.
export async function listenAppResponses(onResponse: ChatResponseCallback) {
  const appId = chatStore.currentAppId.get();
  assert(appId, 'No app id');

  await pollResponses(appId, onResponse, async () => {
    while (true) {
      try {
        await callNutAPI('listen-app-responses', { appId }, onResponse);
        break;
      } catch (error) {
        // Retry with a delay until the message finishes successfully.
        console.log('listenAppResponses error, retrying', error);
        await waitForTime(5000);
        continue;
      }
    }
  });
}
